import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowUpRight, Activity, Users, Wallet, Rocket, AlertCircle, DollarSign, Flame, Dumbbell, FileText, Clock } from "lucide-react";
import QuickWorkoutModal from "@/components/QuickWorkoutModal";

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatTile({ eyebrow, value, sub, accent, testid }) {
    return (
        <div className="border border-[var(--border)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid={testid}>
            <p className="label-tiny mb-3">{eyebrow}</p>
            <div className="flex items-end justify-between">
                <p className="font-display text-6xl leading-none">{value}</p>
                {accent}
            </div>
            {sub && <p className="text-xs text-[var(--text-muted)] mt-3 font-mono">{sub}</p>}
        </div>
    );
}

export default function DashboardPage() {
    const [stats, setStats] = useState(null);
    const [today, setToday] = useState([]);
    const [inactivity, setInactivity] = useState(null);
    const [growth, setGrowth] = useState(null);
    const [inquiries, setInquiries] = useState([]);
    const [onboarding, setOnboarding] = useState(null);
    const [quickOpen, setQuickOpen] = useState(false);

    const reloadToday = async () => {
        const w = await api.get("/workouts", { params: { start: new Date().toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) } });
        setToday(w.data);
    };

    useEffect(() => {
        (async () => {
            const [s, w, inact, g, inq, ob] = await Promise.all([
                api.get("/stats/overview"),
                api.get("/workouts", { params: { start: new Date().toISOString().slice(0, 10), end: new Date().toISOString().slice(0, 10) } }),
                api.get("/growth/inactivity"),
                api.get("/growth/overview"),
                api.get("/coaching-inquiries").catch(() => ({ data: [] })),
                api.get("/documents/onboarding-checklist").catch(() => ({ data: null })),
            ]);
            setStats(s.data);
            setToday(w.data);
            setInactivity(inact.data);
            setGrowth(g.data);
            setInquiries(inq.data || []);
            setOnboarding(ob.data);
        })();
    }, []);

    return (
        <div className="p-8 space-y-8" data-testid="dashboard-page">
            {inactivity?.show_banner && (
                <div className="border border-[var(--warning)] bg-[var(--surface)] p-4 flex items-center justify-between" data-testid="inactivity-banner">
                    <div className="flex items-center gap-3">
                        <AlertCircle size={18} className="text-[var(--warning)]" />
                        <div>
                            <p className="font-medium">Welcome back.</p>
                            <p className="text-xs text-[var(--text-muted)]">You've been away {inactivity.days_inactive} days. Your clients don't coach themselves — pick up where you left off.</p>
                        </div>
                    </div>
                    <Link to="/calendar" className="btn-ghost text-xs">Open calendar →</Link>
                </div>
            )}
            <div className="flex items-end justify-between">
                <div>
                    <p className="label-tiny mb-2">/ Mission control</p>
                    <h1 className="font-serif text-6xl">Overview</h1>
                </div>
                <p className="font-mono text-xs text-[var(--text-muted)]">
                    {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)]">
                <StatTile eyebrow="Active clients" value={stats?.active_clients ?? "—"} sub={`of ${stats?.total_clients ?? "—"} total`} accent={<Users size={20} className="text-[var(--gold)]" />} testid="stat-active-clients" />
                <StatTile eyebrow="This week" value={stats?.week_workouts ?? "—"} sub="sessions assigned" accent={<Activity size={20} className="text-[var(--gold)]" />} testid="stat-week" />
                <StatTile eyebrow="Today" value={stats?.today_workouts ?? "—"} sub="on the schedule" accent={<ArrowUpRight size={20} className="text-[var(--gold-bright)]" />} testid="stat-today" />
                <StatTile eyebrow="Revenue" value={stats ? fmtMoney(stats.revenue_total) : "—"} sub="lifetime, completed" accent={<Wallet size={20} className="text-[var(--gold-bright)]" />} testid="stat-revenue" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 border border-[var(--border)] bg-[var(--surface)] p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <p className="label-tiny mb-1">/ Revenue trend</p>
                            <h3 className="font-serif text-3xl">Last 8 weeks</h3>
                        </div>
                    </div>
                    <div className="h-64 w-full">
                        {stats?.weekly_revenue && stats.weekly_revenue.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={stats.weekly_revenue} margin={{ top: 10, right: 20, bottom: 5, left: -10 }}>
                                    <CartesianGrid stroke="#1a1a1a" strokeDasharray="0" vertical={false} />
                                    <XAxis dataKey="week" stroke="#555" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => v?.slice(5)} />
                                    <YAxis stroke="#555" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                                    <Tooltip
                                        contentStyle={{ background: "#0d0d0d", border: "1px solid #2c2c2c", borderRadius: 0 }}
                                        labelStyle={{ color: "#fff", fontFamily: "JetBrains Mono" }}
                                        formatter={(v) => [fmtMoney(v), "Revenue"]}
                                    />
                                    <Line type="monotone" dataKey="amount" stroke="#C9A24A" strokeWidth={2} dot={{ fill: "#E5C76B", r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full w-full border border-dashed border-[var(--border-bright)] flex flex-col items-center justify-center text-center px-6">
                                <p className="font-serif text-2xl mb-1">No revenue yet</p>
                                <p className="text-xs text-[var(--text-muted)] font-mono">Charge your first client from the Payments page →</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="border border-[var(--border)] bg-[var(--surface)] p-6" data-testid="today-schedule">
                    <p className="label-tiny mb-1">/ Today</p>
                    <h3 className="font-serif text-3xl mb-5">Schedule</h3>
                    {today.length === 0 ? (
                        <div className="border border-dashed border-[var(--border-bright)] p-8 text-center">
                            <p className="text-sm text-[var(--text-muted)]">No sessions today</p>
                            <Link to="/calendar" className="text-[var(--gold)] text-sm mt-2 inline-block">Plan one →</Link>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {today.map((w) => (
                                <div key={w.id} className="border-l-2 border-[var(--gold)] pl-4 py-2">
                                    <p className="font-mono text-xs text-[var(--text-muted)]">{w.time || "—"} · {w.duration_min}m</p>
                                    <p className="font-medium">{w.title}</p>
                                    <p className="text-xs text-[var(--text-muted)]">{w.client_name}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Athlete join link — top-of-mind */}
            {growth?.referral_code && (
                <div className="border border-[var(--gold)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid="dashboard-athlete-link">
                    <div className="absolute inset-0 bg-stripes opacity-20 pointer-events-none" />
                    <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                        <div className="flex-1 min-w-0">
                            <p className="label-tiny mb-1 text-[var(--gold-bright)]">/ Athlete join link · share with new clients</p>
                            <div className="border border-[var(--border-bright)] bg-[var(--bg-deep)] px-3 py-2.5 font-mono text-xs text-[var(--gold-bright)] break-all">
                                {`${window.location.origin}/join/${growth.referral_code}`}
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/join/${growth.referral_code}`)} className="btn-ghost inline-flex items-center gap-2" data-testid="dashboard-copy-athlete-link">
                                <Rocket size={14} /> Copy link
                            </button>
                            <Link to="/growth" className="btn-primary inline-flex items-center gap-2"><Rocket size={14} /> Growth</Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Onboarding checklist — new clients with unsigned docs */}
            {onboarding && onboarding.total_pending > 0 && (
                <OnboardingChecklist data={onboarding} />
            )}

            {/* Quick Workout — primary action */}
            <div className="border border-[var(--gold)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid="dashboard-quick-workout">
                <div className="absolute inset-0 bg-stripes opacity-15 pointer-events-none" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Dumbbell size={14} className="text-[var(--gold-bright)]" />
                            <p className="label-tiny text-[var(--gold-bright)]">/ Build a workout — straight to it</p>
                        </div>
                        <p className="font-serif text-2xl mb-1">Click to build. Save to the client's calendar.</p>
                        <p className="text-xs text-[var(--text-muted)]">Pick a client · drop in exercises from your library (with demos) · ship.</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setQuickOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="dashboard-quick-workout-btn">
                            <Dumbbell size={14} /> Build workout
                        </button>
                    </div>
                </div>
            </div>

            {/* Free Challenge CTA */}
            <div className="border border-[var(--gold)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid="dashboard-challenge-cta">
                <div className="absolute inset-0 bg-stripes opacity-15 pointer-events-none" />
                <div className="relative flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <Flame size={14} className="text-[var(--gold-bright)]" />
                            <p className="label-tiny text-[var(--gold-bright)]">/ Free 2-week challenge</p>
                        </div>
                        <p className="font-serif text-2xl mb-1">Send people to <span className="font-mono text-base">{`${window.location.origin}/challenge`}</span></p>
                        <p className="text-xs text-[var(--text-muted)]">Each enrollment auto-creates a client + schedules 6 workouts (Mon · Wed · Fri).</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/challenge`)} className="btn-ghost inline-flex items-center gap-2" data-testid="dashboard-copy-challenge-link">
                            <Flame size={14} /> Copy link
                        </button>
                        <a href="/challenge" target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="dashboard-view-challenge">
                            <ArrowUpRight size={14} /> View page
                        </a>
                    </div>
                </div>
            </div>


            {/* Public pricing page CTA */}
            <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6" data-testid="dashboard-pricing-cta">
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                    <div className="flex-1">
                        <p className="label-tiny mb-1 text-[var(--gold-bright)]">/ Public pricing page</p>
                        <p className="font-serif text-2xl mb-1">Send paying athletes to <span className="font-mono text-base">{`${window.location.origin}/pricing`}</span></p>
                        <p className="text-xs text-[var(--text-muted)]">Starter $149.99 · Pro $249.99 · Elite $499.99 — every paid checkout shows up below as a new lead.</p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/pricing`)} className="btn-ghost inline-flex items-center gap-2" data-testid="dashboard-copy-pricing-link">
                            <DollarSign size={14} /> Copy link
                        </button>
                        <a href="/pricing" target="_blank" rel="noreferrer" className="btn-primary inline-flex items-center gap-2" data-testid="dashboard-view-pricing">
                            <ArrowUpRight size={14} /> View page
                        </a>
                    </div>
                </div>

                {inquiries.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-[var(--border)]" data-testid="dashboard-inquiries">
                        <p className="label-tiny mb-3">/ {inquiries.length} paid lead{inquiries.length !== 1 ? "s" : ""} · most recent first</p>
                        <div className="divide-y divide-[var(--border)] border border-[var(--border)]">
                            {inquiries.slice(0, 5).map((q) => (
                                <div key={q.id} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`inquiry-${q.id}`}>
                                    <div className="min-w-0">
                                        <p className="font-medium truncate">{q.name || "(no name)"} · <span className="text-[var(--text-muted)] font-mono">{q.email}</span></p>
                                        <p className="text-xs text-[var(--text-muted)] font-mono">{new Date(q.created_at).toLocaleString()}</p>
                                    </div>
                                    <div className="text-right shrink-0 ml-4">
                                        <span className={`tag-pill ${q.plan_id === "elite" ? "text-[var(--gold-bright)]" : q.plan_id === "pro" ? "text-[var(--gold)]" : ""}`}>{q.plan_id}</span>
                                        <p className="text-xs text-[var(--text-muted)] font-mono mt-1">${Number(q.amount || 0).toFixed(2)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Growth CTA */}
            {growth && (
                <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid="dashboard-growth-cta">
                    <div className="absolute inset-0 bg-stripes opacity-20 pointer-events-none" />
                    <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                        <div className="md:col-span-2">
                            <div className="flex items-center gap-3 mb-2">
                                <Rocket size={18} className="text-[var(--gold-bright)]" />
                                <p className="label-tiny text-[var(--gold-bright)]">/ Grow NDE Fitness</p>
                            </div>
                            {growth.next_tier ? (
                                <>
                                    <h3 className="font-serif text-3xl mb-2">
                                        {growth.next_tier.threshold - growth.signups} more signup{growth.next_tier.threshold - growth.signups === 1 ? "" : "s"} to unlock <span className="gold-gradient-text italic">{growth.next_tier.label}</span>
                                    </h3>
                                    <p className="text-sm text-[var(--text-muted)]">Share your referral link. Every coach or athlete who joins moves you closer to unlocked rewards.</p>
                                </>
                            ) : (
                                <h3 className="font-serif text-3xl mb-2">You've maxed out all reward tiers 🏆</h3>
                            )}
                        </div>
                        <div className="flex justify-end">
                            <Link to="/growth" className="btn-primary inline-flex items-center gap-2" data-testid="dashboard-open-growth"><Rocket size={14} /> Open growth tab</Link>
                        </div>
                    </div>
                </div>
            )}

            {/* Coach acquisition loop */}
            <div className="border border-[var(--border)] bg-[var(--surface)] p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="coach-acquisition-cta">
                <div>
                    <p className="label-tiny mb-1">/ For other coaches</p>
                    <h3 className="font-serif text-2xl mb-1">Know a coach hungry for an edge?</h3>
                    <p className="text-sm text-[var(--text-muted)] max-w-xl">Send them to our application — selected coaches get founding-coach pricing and priority AI credits.</p>
                </div>
                <Link to="/apply-coach" className="btn-ghost" data-testid="apply-coach-cta">Apply for Coach Access →</Link>
            </div>

            <QuickWorkoutModal open={quickOpen} onClose={() => setQuickOpen(false)} onSaved={() => reloadToday()} />
        </div>
    );
}

function OnboardingChecklist({ data }) {
    const daysSince = (iso) => {
        if (!iso) return 0;
        const diff = Date.now() - new Date(iso).getTime();
        return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
    };
    return (
        <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6" data-testid="onboarding-checklist">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div>
                    <p className="label-tiny mb-1 text-[var(--gold-bright)]">/ Onboarding · needs attention</p>
                    <h3 className="font-serif text-3xl">
                        {data.total_clients} {data.total_clients === 1 ? "client hasn't" : "clients haven't"} finished onboarding
                    </h3>
                    <p className="text-sm text-[var(--text-muted)] mt-2">
                        {data.total_pending} unsigned {data.total_pending === 1 ? "document" : "documents"} across their intake packets.
                    </p>
                </div>
                <Link to="/documents" className="btn-ghost text-xs inline-flex items-center gap-2 shrink-0">
                    <FileText size={13} /> Open documents
                </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border)]">
                {data.clients.slice(0, 6).map((c) => {
                    const stale = daysSince(c.oldest_pending);
                    const isStale = stale >= 3;
                    return (
                        <Link
                            key={c.client_id}
                            to={`/clients/${c.client_id}`}
                            className="bg-[var(--surface)] hover:bg-[var(--surface-hover)] transition-colors p-4 flex items-center gap-4"
                            data-testid={`onboarding-row-${c.client_id}`}
                        >
                            <div className="w-10 h-10 border border-[var(--gold-deep)] bg-[var(--bg-deep)] flex items-center justify-center flex-shrink-0 font-serif text-lg text-[var(--gold-bright)]">
                                {(c.name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{c.name || "Unnamed"}</p>
                                <p className="text-xs text-[var(--text-muted)] truncate">
                                    {c.pending_docs.map((d) => d.title).join(" · ")}
                                </p>
                            </div>
                            <div className="text-right shrink-0">
                                <p className={`font-display text-2xl leading-none ${isStale ? "text-[var(--warning)]" : "text-[var(--gold-bright)]"}`}>
                                    {c.pending_count}
                                </p>
                                <p className={`text-[10px] font-mono tracking-[0.15em] uppercase mt-1 inline-flex items-center gap-1 ${isStale ? "text-[var(--warning)]" : "text-[var(--text-muted)]"}`}>
                                    <Clock size={9} /> {stale}d
                                </p>
                            </div>
                        </Link>
                    );
                })}
            </div>
            {data.clients.length > 6 && (
                <p className="text-xs text-[var(--text-muted)] font-mono mt-4 text-center">
                    + {data.clients.length - 6} more — see all in Documents
                </p>
            )}
        </div>
    );
}

